import {
  BottomSheet,
  BottomSheetBody,
  BottomSheetHeader,
  Box,
  Button,
  FadeBorder,
  Icon,
  Image,
  MethodCategoryOption,
  PhoneMockup,
  PhoneMockupLayout,
  Text,
} from '@frontend/ui-library';
import { useEffect } from 'react';
import { BillDetailHeader } from '../../components';

import { ReactComponent as ExternalLink2Svg } from '@frontend/icons/external-link-2.svg';
import { ReactComponent as RefreshSvg } from '@frontend/icons/refresh-ccv-rec-duotone.svg';
import { ReactComponent as WalletSvg } from '@frontend/icons/wallet-duotone.svg';
import { getIdFromUrl } from '@frontend/utils';
import { links } from '../../constants';
import { useWidgetContext } from '../../context/WidgetContext';
import { isOfframpBill } from '../../utils';
import { Currency } from '../CurrencySelection/CurrencySelection';
import { useMethodSelection } from './useMethodSelection';
import styles from './MethodSelection.module.css';

export type MethodCategory = 'wallet' | 'exchange' | 'binancePay';

interface MethodSelectionProps {
  billExpiresAt: number;
  merchantName: string;
  amount: string;
  currency: string;
  isBinancePayEnabledForMerchant: boolean;
  eurAmount: string;
  currencies: Currency[];
  travelRuleEnabled: boolean;
  billType?: string | null;
  onSelection: (category: MethodCategory) => void;
  onSelectionError: () => void;
}

export default function MethodSelection({
  billExpiresAt,
  merchantName,
  amount,
  currency,
  isBinancePayEnabledForMerchant,
  eurAmount,
  currencies,
  travelRuleEnabled,
  billType,
  onSelection,
  onSelectionError,
}: MethodSelectionProps): JSX.Element {
  const { configureHeader } = useWidgetContext();

  const {
    loading,
    isBinancePaySelected,
    isBinancePayEnabled,
    isWalletAgreementOpen,
    getBinancePayHelperMessage,
    handleCategorySelection,
    handleAgreementAgree,
    handleAgreementClose,
  } = useMethodSelection({
    onSelectionError,
    onCurrencySelectionRequired: onSelection,
    currencies,
    eurAmount,
    travelRuleEnabled,
  });

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      expiresAt: billExpiresAt,
      allowClosePayment: true,
      merchantName,
      billType,
    });
  }, [billExpiresAt]);

  const isOffRamp = isOfframpBill(billType);

  const getBinancePayDescription = () => {
    if (!isBinancePayEnabled) {
      return '';
    }

    const actionText = isOffRamp ? 'Off ramp' : 'Pay';
    const methodText = isOffRamp ? 'method' : 'payment method';

    return `${actionText} with 100+ cryptocurrencies instantly with zero gas fees. The recommended ${methodText} for Binance users.`;
  };

  return (
    <PhoneMockupLayout>
      <PhoneMockup
        header={
          <BillDetailHeader
            amount={amount}
            currency={currency}
            isOffRamp={isOffRamp}
          />
        }
        bottomSheet={
          <WalletAgreementBottomSheet
            open={isWalletAgreementOpen}
            onRequestClose={handleAgreementClose}
            onAgree={handleAgreementAgree}
          />
        }
      >
        <Text
          mt="0"
          mb="0"
          as="h1"
          size="big"
          weight="700"
          color="royal-700"
          align="center"
        >
          How will you {isOffRamp ? 'off ramp?' : 'pay?'}
        </Text>
        <MethodCategoryOption
          mt="24"
          name="From an exchange"
          icon={<RefreshSvg />}
          description="For funds stored in exchanges such as Binance, Coinbase, Crypto.com or any other."
          stackAssets={[
            { assetSrc: '/assets/exchanges/binance.svg', alt: 'binance' },
            {
              assetSrc: '/assets/exchanges/coinbase.svg',
              alt: 'coinbase',
            },
            { assetSrc: '/assets/exchanges/cryptocom.svg', alt: 'cryptocom' },
          ]}
          onClick={() => handleCategorySelection('exchange')}
          disabled={loading}
          data-test-id="exchangeCategory"
        />
        <MethodCategoryOption
          mt="8"
          name="From a wallet"
          icon={<WalletSvg />}
          description="For funds stored in decentralised wallets like, Metamask, Trust Wallet, xPortal or any other."
          stackAssets={[
            { assetSrc: '/assets/wallets/metamask.svg', alt: 'metamask' },
            {
              assetSrc: '/assets/wallets/walletconnect.svg',
              alt: 'walletconnect',
            },
            { assetSrc: '/assets/networks/erd.svg', alt: 'multiversx' },
          ]}
          onClick={() => handleCategorySelection('wallet')}
          disabled={loading}
          data-test-id="walletCategory"
        />
        {isBinancePayEnabledForMerchant && (
          <MethodCategoryOption
            mt="8"
            name="Binance Pay"
            description={getBinancePayDescription()}
            helperMessage={getBinancePayHelperMessage()}
            icon={<Image src="/assets/exchanges/binance.svg" alt="binance" />}
            onClick={() => handleCategorySelection('binancePay')}
            disabled={!isBinancePayEnabled || loading}
            data-test-id="binancePayCategory"
            loading={loading && isBinancePaySelected}
          />
        )}
        <Button
          mt={{
            XS: '24',
            SM: 'auto',
          }}
          as="a"
          size="medium"
          href={links.support.HOW_TO_PAY}
          target="_blank"
          rel="noopener noreferrer"
          variant="ghost"
          rightIcon={<Icon component={<ExternalLink2Svg />} size={16} />}
        >
          Learn how to {isOffRamp ? 'off ramp' : 'pay'}
        </Button>
      </PhoneMockup>
    </PhoneMockupLayout>
  );
}

const WalletAgreementBottomSheet = ({
  open,
  onRequestClose,
  onAgree,
}: {
  open: boolean;
  onRequestClose: () => void;
  onAgree: () => void;
}) => {
  return (
    <BottomSheet
      open={open}
      onRequestClose={onRequestClose}
      contentClassName={styles['bottom-sheet-content']}
    >
      <BottomSheetHeader>
        <Text color="royal-700" weight="600">
          Wallet ownership
        </Text>
      </BottomSheetHeader>
      <Box ml="24" mr="24">
        <FadeBorder />
      </Box>
      <BottomSheetBody className={styles['bottom-sheet-body']}>
        <Text
          as="p"
          mb="0"
          mt="0"
          align="center"
          size="small"
          color="neutral-500"
        >
          New regulations require wallet ownership validation. <br /> Continuing
          declares you are the owner of the wallet.
        </Text>
        <Button
          fullWidth
          mt="16"
          variant="primary"
          size="large"
          onClick={onAgree}
        >
          Agree and continue
        </Button>
      </BottomSheetBody>
    </BottomSheet>
  );
};
